import rxjs from 'rxjs';

import * as Elevated from '@pitaman71/omniglot-introspect';
import { Pair } from './Mutations/OfMap';
import { Clients, Mutations, Objects } from '.';

export let debugMode = false; 

export class ScalarImage<ValueType> {
    _value: undefined|ValueType;

    update(mutation: Mutations.OfScalar.Mutation<ValueType>) {
        const image = this;
        if(debugMode) console.log(`ScalarImage receives ${JSON.stringify(mutation)}`)
        mutation.dispatch({
            assign(value_: ValueType) {
                if(image._value !== value_) {
                    image._value = value_;
                }
                return this;
            },
            clear() {
                if(image._value !== undefined) {
                    image._value = undefined;
                }
                return this;
            }
        });
    }

    prime(prev: Elevated.Reference<Mutations.OfScalar.Mutation<ValueType>>): Mutations.OfScalar.Mutation<ValueType> {
        if(debugMode) console.log(`ScalarImage is priming with value = ${JSON.stringify(this._value)}`)
        if(this._value === undefined) {
            return Mutations.OfScalar.Mutation.fromClear(prev).prime();
        } else {
            return Mutations.OfScalar.Mutation.fromAssign(prev, this._value).prime();
        }
    }
}

export class SetImage<ValueType> {
    _values: Set<ValueType> = new Set();

    static isEqual<ValueType>(a: Set<ValueType>, b: Set<ValueType>, cmp?: (a: ValueType,b: ValueType) => boolean) {
        if(a.size !== b.size) return false;
        const ax = 
            cmp !== undefined
            ? Array.from(a).filter(av => Array.from(b).filter(bv => cmp(av, bv)).length === 0)
            : Array.from(a).filter(_value => !b.has(_value));
        const bx = 
            cmp !== undefined
            ? Array.from(b).filter(bv => Array.from(a).filter(av => cmp(av, bv)).length === 0)
            : Array.from(b).filter(value_ => !a.has(value_));
        return(ax.length === 0 && bx.length === 0);
    }

    update(mutation: Mutations.OfSet.Mutation<ValueType>) {
        const image = this;
        mutation.dispatch(new class {
            clear() {
                if(image._values.size !== 0) {
                    image._values = new Set();
                }
                return this;
            }
            assign(values_: Set<ValueType>) {
                if(!SetImage.isEqual(image._values, values_)) {
                    image._values = new Set(values_);
                }
                return this;
            }
            insert(...values_: Array<ValueType>) {
                const added = values_.filter(value_ => !image._values.has(value_));
                if(added.length > 0) {
                    added.forEach(value_ => image._values.add(value_));
                }
                return this;
            }
            remove(...values_: Array<ValueType>) {
                const removed = values_.filter(value_ => image._values.has(value_));
                if(removed.length > 0) {
                    removed.forEach(value_ => image._values.delete(value_));
                }
                return this;
            }
        })
    }
    prime(prev: Elevated.Reference<Mutations.OfSet.Mutation<ValueType>>): Mutations.OfSet.Mutation<ValueType> {
        if(this._values.size === 0) {
            return Mutations.OfSet.Mutation.fromClear(prev).prime();
        } else {
            return Mutations.OfSet.Mutation.fromAssign(prev, ...this._values).prime();
        }
    }
}

export class SequenceImage<ValueType> {
    _values: Array<ValueType> = [];

    static isEqual<ValueType>(a: Array<ValueType>, b: Array<ValueType>, cmp?: (a: ValueType,b: ValueType) => boolean) {
        if(a.length !== b.length) return false;
        for(let i = 0; i < a.length;i++) {
            if(cmp !== undefined) {
                if(cmp(a[i], b[i]) === false) return false;
            } else if(a[i] !== b[i]) {
                return false;
            }
        }
        return true;
    }

    update(mutation: Mutations.OfSequence.Mutation<ValueType>) {
        const image = this;
        if(debugMode) console.log(`SequenceImage.update WITH ${mutation}`)
        if(debugMode) console.log(`SequenceImage.update BEFORE = ${this._values}`)
        mutation.dispatch(new class {                
            clear() {
                if(image._values.length !== 0) {
                    image._values = [];
                }
                return this;
            }
            assign(values_: ValueType[]) {
                if(!SequenceImage.isEqual(image._values, values_)) {
                    image._values = [... values_];
                }
                return this;
            }
            remove(...values_: ValueType[]) {
                const removed = new Set(values_);
                image._values = image._values.filter(_value => !removed.has(_value));
                return this;
            }
            append(after: ValueType|undefined, ...values_: ValueType[]) {
                const appended = 
                    image._values.length === 0 || after === undefined 
                    ? [ ...image._values, ...values_]
                    : image._values.reduce<ValueType[]>((appended, _value) =>
                        _value === after
                        ? [ ...appended, _value, ...values_ ] 
                        : [ ...appended, _value]
                , []);
                if(appended.length !== image._values.length) {
                    image._values = appended;
                }
                return this;
            }
            prepend(before: ValueType|undefined, ...values_: ValueType[]) {
                const prepended = 
                    image._values.length === 0 || before === undefined 
                    ? [ ...values_, ...image._values ]
                    : image._values.reduce<ValueType[]>((prepended, _value) =>
                        _value === before
                        ? [ ...prepended, ...values_, _value ] 
                        : [ ...prepended, _value]
                , []);
                if(prepended.length !== image._values.length) {
                    image._values = prepended;
                }
                return this;
            }
        })
        if(debugMode) console.log(`SequenceImage.update AFTER = ${this._values}`)
    }

    prime(prev: Elevated.Reference<Mutations.OfSequence.Mutation<ValueType>>): Mutations.OfSequence.Mutation<ValueType> {
        if(this._values.length === 0) {
            return Mutations.OfSequence.Mutation.fromClear(prev).prime();
        } else {
            return Mutations.OfSequence.Mutation.fromAssign(prev, ...this._values).prime();
        }
    }

}

export class MapImage<KeyType, ValueType> {
    _pairs: Array<Pair<KeyType, ValueType>> = [];

    static isEqual<KeyType, ValueType>(a: Pair<KeyType, ValueType>[], b: Pair<KeyType, ValueType>[], cmp?: (a: ValueType|undefined,b: ValueType|undefined) => boolean) {
        if(a.length !== b.length) return false;
        const aKeys = new Set(a.map(item => item.key));
        const bKeys = new Set(b.map(item => item.key))
        if(!SetImage.isEqual(aKeys, bKeys)) return false;

        for(let key of aKeys) {
            const aValues = a.filter(pair => pair.key === key).map(pair => pair.value);
            const bValues = b.filter(pair => pair.key === key).map(pair => pair.value);
            if(!SequenceImage.isEqual(aValues, bValues, cmp)) return false;
        }
        return true;
    }

    update(mutation: Mutations.OfMap.Mutation<KeyType, ValueType>) {
        const image = this;
        mutation.dispatch(new class {                
            clear() {
                if(image._pairs.length !== 0) {
                    image._pairs = [];
                }
                return this;
            }
            assign(... pairs_: Pair<KeyType, ValueType>[]) {
                if(!MapImage.isEqual(image._pairs, pairs_)) {
                    image._pairs = [...pairs_];
                }
                return this;
            }
            insert(... pairs_: Pair<KeyType,ValueType>[]) {
                const added = pairs_.filter(pair => image._pairs.filter(_pair => _pair.key === pair.key || _pair.value === pair.value).length === 0);
                if(added.length > 0) {
                    image._pairs = [...image._pairs, ...added];
                }
                return this;
            }
            remove(... pairs_: Pair<KeyType,ValueType>[]) {
                const remainder = image._pairs.filter(pair => pairs_.filter(_pair => _pair.key === pair.key || _pair.value === pair.value).length === 0);
                if(remainder.length != image._pairs.length) {
                    image._pairs = remainder;
                }
                return this;
            }
            replace(key: KeyType, ...values_: ValueType[]) {
                return this;
            }
        })
    }

    prime(prev: Elevated.Reference<Mutations.OfMap.Mutation<KeyType, ValueType>>): Mutations.OfMap.Mutation<KeyType, ValueType> {
        if(this._pairs.length === 0) {
            return Mutations.OfMap.Mutation.fromClear(prev).prime();
        } else {
            return Mutations.OfMap.Mutation.fromAssign(prev, ...this._pairs).prime();
        }
    }

}

export class RelationImage<BindingType extends Objects.BindingType> {
    _entries: Array<BindingType> = [];

    static isEqual<BindingType>(a: BindingType[], b: BindingType[]) {
        const ax = a.filter((ae: any) => b.filter((be: any) => MapImage.isEqual(
            Object.entries(ae).map(([key, value]) => Mutations.OfMap.Pair.from( key, value )),
            Object.entries(be).map(([key, value]) => Mutations.OfMap.Pair.from( key, value ))
        )).length === 0);
        const bx = b.filter((be: any) => a.filter((ae: any) => MapImage.isEqual(
            Object.entries(ae).map(([key, value]) => Mutations.OfMap.Pair.from( key, value )),
            Object.entries(be).map(([key, value]) => Mutations.OfMap.Pair.from( key, value ))
        )).length === 0);
        return ax.length === 0 && bx.length === 0;
    }

    update(mutation: Mutations.OfRelation.Mutation<BindingType>) {
        const image = this;
        if(debugMode) console.log(`RelationImage receives ${JSON.stringify(mutation)}`)
        mutation.dispatch(new class implements Clients.RelationClient<BindingType> {
            clear() {
                if(image._entries.length !== 0) {
                    image._entries = [];
                }
                return this;
            }
            assign(entries_: BindingType[]) {
                if(debugMode) console.log(`RelationImage receives assignment to ${JSON.stringify(entries_)}`)
                if(!RelationImage.isEqual(image._entries, entries_)) {
                    if(debugMode) console.log(`RelationImage accepts assignment to ${JSON.stringify(entries_)}`)
                    image._entries = [...entries_];
                }
                return this;
            }
            insert(...entries_: BindingType[]) {
                if(debugMode) console.log(`RelationImage receives insert to ${JSON.stringify(entries_)}`)
                const result = entries_.reduce((prev, entry_, index) => (
                    Objects.contains(prev, entry_) ? prev : [ ...prev, entry_]
                ), image._entries);
                image._entries = result;
                return this;
            }
            remove(...entries_: BindingType[]) {
                const result = (image._entries || []).filter(_entry => !Objects.contains(entries_, _entry));
                image._entries = result;
                return this;
            }
        })
    }
    prime(prev: Elevated.Reference<Mutations.OfRelation.Mutation<BindingType>>): Mutations.OfRelation.Mutation<BindingType> {
        if(debugMode) console.log(`RelationImage is priming with entries = ${JSON.stringify(this._entries)}`)
        if(this._entries.length === 0) {
            return Mutations.OfRelation.Mutation.fromClear(prev).prime();
        } else {
            return Mutations.OfRelation.Mutation.fromAssign(prev, ...this._entries).prime();
        }
    }
}
